import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader } from 'lucide-react';
import { getWorksCategoryPanels } from '../services/api';

const WorksCategory = () => {
  const navigate = useNavigate();
  const [activePanel, setActivePanel] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null); // Track hovered item for 3D effect
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  // 从API获取分类数据
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        const data = await getWorksCategoryPanels();
        setCategories(data || []);
      } catch (error) {
        console.error('❌ 获取作品分类失败:', error);
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const getPanelWidth = (panelId) => {
    if (!activePanel) return `${100 / categories.length}%`;
    if (activePanel === panelId) return '70%'; // 展开状态
    return `${30 / Math.max(categories.length - 1, 1)}%`; // 其余面板平分剩余空间
  };

  const handlePanelClick = (panelId) => {
    setActivePanel((current) => (current === panelId ? null : panelId));
  };

  // Loading状态
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-gray-300 text-sm">正在加载分类...</p>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-white font-medium">作品分类尚未配置</p>
          <p className="text-gray-400 text-sm mt-2">请先在 CMS 中添加作品分类与面板内容</p>
        </div>
      </div>
    );
  }


  return (
    <div className="fixed inset-0 bg-black">
      {/* Content Container - Full Screen */}
      <div className="absolute inset-0 flex">
        {categories.map((category, index) => {
          const isActive = activePanel === category.id;
          // 未展开时所有卡片保持横排；仅在有展开卡后，其余收起卡才变为竖排。
          const isCollapsed = Boolean(activePanel) && !isActive;
          // 中文按单字移动，连续英文/数字视为一个词，避免 AIGC 被拉成 A I G C。
          const titleGlyphs = (category.title || '').match(/[\u3400-\u9fff]|[A-Za-z0-9]+|[^\s]/g) || [];
          const glyphWidths = titleGlyphs.map((glyph) => /^[A-Za-z0-9]+$/.test(glyph)
            ? Array.from(glyph).length * 0.62
            : 1);
          const totalGlyphWidth = glyphWidths.reduce((total, width) => total + width, 0);
          let glyphCursor = -totalGlyphWidth / 2;
          return (
          <div
            key={category.id}
            className="relative transition-[width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] cursor-pointer overflow-hidden"
            style={{ width: getPanelWidth(category.id), willChange: 'width' }}
            onClick={() => handlePanelClick(category.id)}
          >
            {/* Background Image - Full Height Including Top */}
            <div 
              className="absolute inset-0 transition-transform duration-500 ease-out"
              style={{ 
                backgroundImage: category.backgroundImage ? `url(${category.backgroundImage})` : 'none',
                backgroundColor: '#111827',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                transform: isActive ? 'scale(1.05)' : 'scale(1)'
              }}
            />
            
            {/* 以合成层透明度替代大图滤镜，避免手机端在伸缩时掉帧。 */}
            <div 
              className="absolute inset-0 bg-black transition-opacity duration-500 ease-out"
              style={{ opacity: isActive ? 0.4 : isCollapsed ? 0.7 : 0.5 }}
            />
            
            {/* Panel Content - Add padding-top to avoid nav */}
            <div className="relative h-full flex flex-col items-center justify-center px-8 pt-16">
              {/* 同一组文字以 transform 在横排与竖排之间移动；不使用 writing-mode，避免移动端突然重排。 */}
              <h2
                aria-label={category.title}
                className={`absolute top-1/2 left-1/2 z-10 h-0 w-0 text-white font-medium leading-none pointer-events-none transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  isActive
                    ? '-translate-x-1/2 -translate-y-40 scale-125'
                    : '-translate-x-1/2 -translate-y-1/2 scale-100'
                }`}
                style={{
                  fontSize: 'clamp(1.25rem, 5.5vw, 1.75rem)',
                  opacity: isCollapsed ? 0.72 : 1,
                  willChange: 'transform, opacity'
                }}
              >
                {titleGlyphs.map((glyph, glyphIndex) => {
                  const glyphWidth = glyphWidths[glyphIndex];
                  const offset = glyphCursor + glyphWidth / 2;
                  glyphCursor += glyphWidth;
                  const isLatinWord = /^[A-Za-z0-9]+$/.test(glyph);
                  return (
                    <span
                      key={`${category.id}-${glyphIndex}`}
                      aria-hidden="true"
                      className="absolute left-0 top-0 inline-block transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                      style={{
                        transform: !isCollapsed
                          ? `translate3d(${offset}em, 0, 0) translate(-50%, -50%)`
                          : `translate3d(0, ${offset}em, 0) translate(-50%, -50%)${isLatinWord ? ' rotate(90deg)' : ''}`,
                        willChange: 'transform'
                      }}
                    >
                      {glyph}
                    </span>
                  );
                })}
              </h2>
              
              {isActive && (
                <div className="flex flex-col items-center mt-20"
                style={{
                  perspective: '1000px'
                }}>
                  {category.items.map((item, index) => {
                    const totalItems = category.items.length;
                    const middleIndex = Math.floor(totalItems / 2);
                    const distanceFromCenter = index - middleIndex;
                    const isHovered = hoveredItem === `${category.id}-${index}`;
                    
                    // Calculate 3D transform values
                    const rotateX = distanceFromCenter * 8;
                    const translateY = distanceFromCenter * 5;
                    const translateZ = Math.abs(distanceFromCenter) * -20;
                    const scale = isHovered ? 1.15 : (1 - Math.abs(distanceFromCenter) * 0.1);
                    const opacity = isHovered ? 1 : (1 - Math.abs(distanceFromCenter) * 0.15);
                    
                    // Staggered fade-in animation
                    const animationDelay = index * 50; // 50ms delay between each item
                    
                    return (
                      <div
                        key={index}
                        className="block relative text-center py-[10px] animate-fadeInUp cursor-pointer"
                        style={{
                          transform: `rotateX(${rotateX}deg) translateY(${translateY}px) translateZ(${translateZ}px) scale(${scale})`,
                          opacity: opacity,
                          transformOrigin: 'center center',
                          animationDelay: `${animationDelay}ms`,
                          animationFillMode: 'both',
                          transition: 'all 0.5s ease-out'
                        }}
                        onMouseEnter={() => setHoveredItem(`${category.id}-${index}`)}
                        onMouseLeave={() => setHoveredItem(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/category/${item.categoryId}`);
                        }}
                      >
                        <span className={`inline-block whitespace-nowrap transition-all duration-300 ${
                          item.highlight || isHovered
                            ? 'text-white font-bold text-lg' 
                            : 'text-white/80 text-base'
                        } ${
                          isHovered ? 'drop-shadow-[0_0_15px_rgba(255,255,255,0.6)]' : ''
                        }`}>
                          {item.name}
                        </span>
                        {isHovered && (
                          <ChevronRight className="inline-block ml-2 w-4 h-4 text-white transition-all duration-300" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Click Hint */}
              {!activePanel && (
                <div className="absolute bottom-10 text-white/40 text-xs">
                  点击展开
                </div>
              )}
            </div>
            
            {/* Vertical Divider */}
            {index < categories.length - 1 && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 h-1/2 w-px bg-white/20" />
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorksCategory;
